import React, { useEffect } from 'react';
import { Form, Select } from 'antd';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { getAlerts } from '@/api/alerts';
import { useAuth } from '@/contexts/AuthContext';
import type { Alert } from '@/types/community/alert';

interface AlertSelectorProps {
  disabled?: boolean;
  fieldName?: string;
  onAlertDataChange?: (alert: { id: string; name: string } | null) => void;
}

export const AlertSelector: React.FC<AlertSelectorProps> = ({
  disabled = false,
  fieldName = 'alertId',
  onAlertDataChange
}) => {
  const { t } = useTranslation(['alerts', 'common']);
  const { firestoreUser } = useAuth();
  const form = Form.useFormInstance();

  // Fetch alerts
  const { data: alertsData, error } = useQuery({
    queryKey: ['alerts', firestoreUser?.workspace?.id],
    queryFn: () => {
      if (!firestoreUser) throw new Error('User not authenticated');
      return getAlerts({ 
        firestoreUser,
        pageSize: 100 // Get all alerts for selector
      });
    },
    enabled: !!firestoreUser?.workspace?.id,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Initialize alert data from form values when component mounts or form values change
  useEffect(() => {
    const alertId = form.getFieldValue(fieldName);
    if (alertId && alertsData?.alerts) {
      const alert = alertsData.alerts.find((a: Alert) => a.id === alertId);
      onAlertDataChange?.(alert ? { 
        id: alert.id, 
        name: alert.title 
      } : null);
    }
  }, [form, alertsData, onAlertDataChange, fieldName]);

  const handleAlertChange = (alertId: string) => {
    // Provide alert data to parent
    if (alertId) {
      const alert = alertsData?.alerts?.find((a: Alert) => a.id === alertId);
      onAlertDataChange?.(alert ? { 
        id: alert.id, 
        name: alert.title 
      } : null);
    } else {
      onAlertDataChange?.(null);
    }
  };

  const alertOptions = alertsData?.alerts?.map((alert: Alert) => ({
    value: alert.id,
    label: alert.title,
  })) || [];

  // Show error state if API fails
  if (error) {
    return (
      <Form.Item
        name={fieldName}
        label={t('alerts:fields.title')}
      >
        <Select
          placeholder={t('alerts:fields.selectAlert')}
          disabled={true}
          options={[]}
        />
      </Form.Item>
    );
  }

  return (
    <Form.Item
      name={fieldName}
      label={t('alerts:fields.title')}
    >
      <Select
        placeholder={t('alerts:fields.selectAlert')}
        onChange={handleAlertChange}
        disabled={disabled}
        allowClear
        options={alertOptions}
        showSearch
        filterOption={(input, option) =>
          (option?.label ?? '').toString().toLowerCase().includes(input.toLowerCase())
        }
      />
    </Form.Item>
  );
}; 